/**
 * Debug Helper Utility
 * 
 * This utility provides debugging functions to help diagnose environment-specific issues.
 */
import { getApiBaseUrl, getAuthApiBaseUrl, getCurrentConfig } from './apiConfig';

// Simple environment detection
const detectEnvironment = () => {
  if (typeof window === 'undefined') return 'staging';
  const hostname = window.location.hostname;
  if (hostname === 'app.sequenceplanner.com' || hostname === 'sequenceplanner.com') {
    return 'production';
  }
  return 'staging';
};

/**
 * Logs environment and API configuration information
 * Useful for debugging environment-specific issues
 * @returns The current configuration
 */
export const logEnvironmentInfo = (): ReturnType<typeof getCurrentConfig> => {
  const log = (label: string, value: string | boolean | undefined) => {
    console.log(`%c${label}:%c ${value}`, 'font-weight: bold; color: #0077cc', 'color: #333');
  };
  
  console.group('%cEnvironment Debug Information', 'font-weight: bold; font-size: 14px; color: #0077cc');
  
  // Get current config
  const config = getCurrentConfig();
  
  // Log environment information
  log('Detected Environment', config.environment);
  log('Development Mode', config.environmentDetails.isDev);
  log('Vite Mode', config.environmentDetails.mode);
  log('Hostname', config.environmentDetails.hostname);
  
  // Log API configuration
  log('Main API Base URL', config.urls.main);
  log('Auth API Base URL', config.urls.auth);
  
  // Log a test API call URL
  log('Example login endpoint', `${config.urls.auth}/auth/magic-link`);
  
  console.groupEnd();
  
  return config;
};

/**
 * Add environment debug info to window for console debugging
 */
export const exposeDebugInfo = (): void => {
  // Always expose debug tools in all environments, but change how they're presented
  const environment = detectEnvironment();
  const isDev = environment === 'development';
  
  // Add debug tools to window
  (window as any).__debugInfo = {
    getConfig: getCurrentConfig,
    getEnvironment: detectEnvironment,
    getUrls: () => ({
      main: getApiBaseUrl(),
      auth: getAuthApiBaseUrl()
    }),
    logInfo: logEnvironmentInfo,
    testConnection: async () => {
      const authUrl = getAuthApiBaseUrl();
      console.log(`Testing connection to ${authUrl}/health...`);
      try {
        const response = await fetch(`${authUrl}/health`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        if (response.ok) {
          console.log('✅ Connection successful!');
          try {
            const data = await response.json();
            console.log('Response data:', data);
            return { success: true, data };
          } catch (e) {
            console.log('Response received but not JSON:', await response.text());
            return { success: true, data: null };
          }
        } else {
          console.error('❌ Connection failed with status:', response.status);
          return { success: false, status: response.status };
        }
      } catch (error) {
        console.error('❌ Connection error:', error);
        return { success: false, error };
      }
    },
    // Add a way to manually show the debug panel if needed
    showDebugPanel: () => {
      const config = getCurrentConfig();
      const debugDiv = document.createElement('div');
      debugDiv.style.position = 'fixed';
      debugDiv.style.top = '0';
      debugDiv.style.left = '0';
      debugDiv.style.width = '100%';
      debugDiv.style.padding = '10px';
      debugDiv.style.background = 'rgba(0,0,0,0.85)';
      debugDiv.style.color = 'white';
      debugDiv.style.zIndex = '9999';
      debugDiv.style.fontFamily = 'monospace';
      debugDiv.style.fontSize = '12px';
      debugDiv.style.maxHeight = '80%';
      debugDiv.style.overflow = 'auto';
      debugDiv.innerHTML = `<h2 style="margin-top:0">Environment Debug Panel</h2>
        <p><strong>Environment:</strong> ${config.environment}</p>
        <p><strong>Hostname:</strong> ${config.environmentDetails.hostname}</p>
        <p><strong>API URLs:</strong></p>
        <pre>${JSON.stringify(config.urls, null, 2)}</pre>`;
      
      // Add close button
      const closeBtn = document.createElement('button');
      closeBtn.innerText = 'Close';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '10px';
      closeBtn.style.right = '10px';
      closeBtn.style.padding = '5px 10px';
      closeBtn.style.background = '#333';
      closeBtn.style.color = 'white';
      closeBtn.style.border = 'none';
      closeBtn.style.borderRadius = '4px';
      closeBtn.onclick = () => document.body.removeChild(debugDiv);
      debugDiv.appendChild(closeBtn);
      
      document.body.appendChild(debugDiv);
      return config;
    }
  };
  
  // Only show the prominent console message in development environments
  if (isDev) {
    console.log(
      '%cEnvironment debug info available! Type in console:\n' +
      '• __debugInfo.getConfig() - Current configuration\n' +
      '• __debugInfo.logInfo() - Log detailed configuration\n' +
      '• __debugInfo.testConnection() - Test API connection\n' +
      '• __debugInfo.showDebugPanel() - Show visual debug panel',
      'background: #0077cc; color: white; padding: 4px 6px; border-radius: 4px; font-weight: bold; line-height: 1.5;'
    );
  } else {
    // Minimalist message for production/staging
    console.log('Debug tools available via window.__debugInfo');
  }
}; 